import { useCallback, useEffect, useState } from "react";
import { MessageSquare, Loader2, RefreshCw, AlertTriangle, Users, X } from "lucide-react";
import api from "../../api";
import Portal from "../common/Portal";

// The online session's transcript (backend meeting/transcription/jobs.py), on the
// consultation page. It's made in the background after the call, so this
// loads once and has a Refresh button rather than polling. Shows nothing for
// a session that was never recorded. A call that dropped and was rejoined
// comes back as one transcript (the pieces are joined first), with a marker
// where each reconnect happened.
//
// Speakers come back from transcription as speaker_0, speaker_1...; the session
// assistant works out which is the clinician (speaker_roles), and "Who's who"
// lets the clinician correct it, which redrafts the session drafts
// (onAssistChange gets the new drafts state). Uploaded scripts and virtual-patient
// sessions already know who is who and never use this panel.
//
// It's a small header button (the page's message icon for call sessions) with a
// dot for its state, opening the transcript in a window. onAvailable(bool) tells
// the page whether this session has a call transcript at all.
const STATUS = {
  recording: "The call is being recorded.",
  waiting: "The transcript starts once the session's time is over, and is usually ready about 30 minutes after that.",
  pending: "Transcript being prepared. It's usually ready within about 30 minutes.",
  transcribing: "Transcript being prepared...",
  failed: "Automatic transcription didn't work for this session. The recording is safe.",
  skipped: "Not transcribed: the patient withdrew their consent after the call.",
  no_audio: "No audio was saved for this call.",
};

// True if a reconnect point falls between the previous segment and this one.
const reconnectBefore = (t, s, prev) => s.start != null && (t.reconnects || []).some(
  (at) => at <= s.start + 0.5 && (prev?.start == null ? false : prev.start < at - 0.5));

const ROLE_LABEL = { clinician: "Clinician", patient: "Patient", other: "Other person" };

const fmtTime = (s) => (s == null ? "" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);

const DOT = { ready: "bg-emerald-500", waiting: "bg-amber-400", failed: "bg-red-500" };

export default function SessionTranscript({ appointmentId, onAssistChange, onAvailable }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // {speaker: role} while "Who's who" is open
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");

  const load = useCallback(() => {
    if (!appointmentId) return;
    setBusy(true);
    api.getAppointmentTranscript(appointmentId)
      .then((r) => setItems(r.data?.transcripts || []))
      .catch(() => setItems([]))
      .finally(() => setBusy(false));
  }, [appointmentId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onAvailable && onAvailable(Boolean(items && items.length)); }, [items, onAvailable]);

  const retry = () => {
    setBusy(true);
    api.retryAppointmentTranscript(appointmentId)
      .then((r) => setItems(r.data?.transcripts || []))
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const saveRoles = () => {
    setBusy(true); setSaveErr(""); setSaveMsg("");
    api.retagTranscriptSpeakers(appointmentId, editing)
      .then((r) => {
        setItems(r.data?.transcripts || []);
        setEditing(null);
        const res = r.data?.result;
        setSaveMsg(!res ? "No change." : res.message || "Saved. The session drafts are being redone with these speakers.");
        onAssistChange && r.data?.assist && onAssistChange(r.data.assist);
      })
      .catch((e) => setSaveErr(e.response?.data?.error || "Couldn't save. Please try again."))
      .finally(() => setBusy(false));
  };

  if (!items || items.length === 0) return null;
  const done = items.filter((t) => t.status === "done");
  const waiting = items.filter((t) => ["recording", "waiting", "pending", "transcribing"].includes(t.status));
  const failed = items.filter((t) => t.status === "failed");
  // Speakers come back as speaker_0, speaker_1..., the same people for the whole session.
  const speakerNo = (speaker) => `Speaker ${Number(String(speaker).replace(/\D/g, "") || 0) + 1}`;
  const roles = done[0]?.speaker_roles || null;
  const label = (speaker) => ROLE_LABEL[roles?.[speaker]] || speakerNo(speaker);
  const speakers = done[0] ? [...new Set((done[0].segments || []).map((s) => s.speaker))].sort() : [];
  const firstWords = (speaker) => {
    const seg = (done[0]?.segments || []).find((s) => s.speaker === speaker && s.text.split(" ").length > 3);
    return seg ? `"${seg.text.slice(0, 90)}${seg.text.length > 90 ? "..." : ""}"` : "";
  };

  const state = done.length > 0 ? "ready" : failed.length > 0 ? "failed" : "waiting";
  const statusLine = done.length > 0
    ? `Ready${done[0].language_code ? ` · ${done[0].language_code.toUpperCase()}` : ""}${done[0].word_count ? ` · ${done[0].word_count} words` : ""}${waiting.length > 0 ? " · more on the way" : ""}`
    : STATUS[(failed[0] || waiting[0] || items[0]).status] || "";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={`Session transcript: ${statusLine}`} aria-label="Session transcript"
        className="relative inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary-600 shadow-sm transition-all hover:bg-primary-50">
        <MessageSquare className="h-3.5 w-3.5" />
        Transcript
        <span className={`h-2 w-2 rounded-full ${DOT[state]}`} aria-hidden="true" />
      </button>

      {open && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Session transcript">
            <div className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="flex items-center gap-3 border-b border-secondary-100 px-5 py-3">
                <MessageSquare className="h-5 w-5 shrink-0 text-primary-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-secondary-900">Session transcript</p>
                  <p className="text-xs text-secondary-600">{statusLine}</p>
                </div>
                {failed.length > 0 && (
                  <button type="button" onClick={retry} disabled={busy}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-700 disabled:opacity-60">
                    Try again
                  </button>
                )}
                {waiting.length > 0 && (
                  <button type="button" onClick={load} disabled={busy} aria-label="Refresh"
                    className="rounded-lg border border-secondary-200 p-1.5 text-secondary-600 hover:bg-secondary-50 disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </button>
                )}
                <button type="button" onClick={() => { setOpen(false); setEditing(null); }} aria-label="Close"
                  className="rounded-full p-1.5 text-secondary-500 hover:bg-secondary-100">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="overflow-y-auto">
                {done.length > 0 && speakers.length > 0 && (
                  <div className="border-b border-secondary-100 px-5 py-3">
                    {!editing ? (
                      <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-700">
                        <Users className="h-4 w-4 text-secondary-500" />
                        {roles
                          ? <span>{speakers.map((sp) => `${speakerNo(sp)} is the ${(ROLE_LABEL[roles[sp]] || "?").toLowerCase()}`).join(", ")}
                              {done[0].roles_source === "doctor" ? " (set by you)." : " (worked out by AI)."}</span>
                          : <span>Speakers aren't identified yet.</span>}
                        <button type="button" onClick={() => { setSaveMsg(""); setEditing(Object.fromEntries(speakers.map((sp) => [sp, roles?.[sp] || ""]))); }}
                          className="font-semibold text-primary-700 hover:underline">{roles ? "Wrong? Change" : "Set who's who"}</button>
                        {saveMsg && <span className="w-full text-secondary-600">{saveMsg}</span>}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs font-bold text-secondary-900">Who's who</p>
                        {speakers.map((sp) => (
                          <div key={sp} className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="w-20 font-semibold text-secondary-700">{speakerNo(sp)}</span>
                            <select value={editing[sp]} onChange={(e) => setEditing((x) => ({ ...x, [sp]: e.target.value }))}
                              className="rounded-lg border border-secondary-300 px-2 py-1 text-xs">
                              <option value="">Choose...</option>
                              <option value="clinician">Clinician</option>
                              <option value="patient">Patient</option>
                              <option value="other">Other person</option>
                            </select>
                            <span className="min-w-0 flex-1 truncate italic text-secondary-500">{firstWords(sp)}</span>
                          </div>
                        ))}
                        {saveErr && <p className="text-xs text-red-700">{saveErr}</p>}
                        <div className="flex gap-2">
                          <button type="button" onClick={saveRoles} disabled={busy || Object.values(editing).some((v) => !v)}
                            className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-700 disabled:opacity-60">Save</button>
                          <button type="button" onClick={() => { setEditing(null); setSaveErr(""); }}
                            className="rounded-lg border border-secondary-200 px-3 py-1.5 text-xs font-semibold text-secondary-700">Cancel</button>
                        </div>
                        <p className="text-[11px] text-secondary-500">Saving redoes the session drafts with the right speakers.</p>
                      </div>
                    )}
                  </div>
                )}

                {done.length === 0 && <p className="px-5 py-6 text-sm text-secondary-600">{statusLine}</p>}

                {done.map((t) => (
                  <div key={t.id} className="px-5 py-3">
                    {t.quality_note && (
                      <p className="mb-2 flex items-start gap-1.5 text-xs text-amber-800">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t.quality_note}
                      </p>
                    )}
                    <p className="mb-2 text-[11px] text-secondary-500">
                      Made automatically, so expect some mistakes. Check anything important against your memory of the session.
                    </p>
                    <div className="space-y-2">
                      {(t.segments || []).map((s, i, all) => (
                        <div key={i}>
                          {reconnectBefore(t, s, all[i - 1]) && (
                            <p className="my-2 flex items-center gap-2 text-[11px] font-semibold text-amber-700">
                              <span className="h-px flex-1 bg-amber-200" /> Call reconnected <span className="h-px flex-1 bg-amber-200" />
                            </p>
                          )}
                          <div className="text-sm">
                            <span className="mr-2 text-[11px] font-semibold text-secondary-500">
                              {label(s.speaker)}{s.start != null ? ` · ${fmtTime(s.start)}` : ""}
                            </span>
                            <span className="text-secondary-800">{s.text}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
